// Central definitions for status enums.
//
// Each enum is declared once here as:
//   - a `*_STATUSES` tuple   → feeds `z.enum(...)` in forms and the DB schema's type
//   - a `*_STATUS_OPTIONS`    → feeds `EnumSelect` (value + human label)
//
// Keeping both in one place means a new status is added in exactly one spot and
// every form, filter, and create dialog picks it up.

export interface EnumOption {
  value: string;
  label: string;
}

// ── Widget (the reference entity — see src/db/schema/widget.ts) ──
//
// `widget` is the template's one example entity. When you add a real entity,
// declare its statuses here the same way and import them in its form/filter.
export const WIDGET_STATUSES = ["draft", "active", "archived"] as const;
export type WidgetStatus = (typeof WIDGET_STATUSES)[number];
export const WIDGET_STATUS_OPTIONS: readonly EnumOption[] = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
];

// ── PTO Entry (src/db/schema/pto-entry.ts) ──
export const PTO_ENTRY_TYPES = ["vacation", "sick", "other"] as const;
export const PTO_ENTRY_TYPE_OPTIONS: readonly EnumOption[] = [
  { value: "vacation", label: "Vacation" },
  { value: "sick", label: "Sick" },
  { value: "other", label: "Other" },
];

// A `cancelled` entry represents a refunded/unused day and MUST be excluded
// from any future balance/allowance-remaining calculation (a separate epic).
export const PTO_ENTRY_STATUSES = ["planned", "taken", "cancelled"] as const;
export const PTO_ENTRY_STATUS_OPTIONS: readonly EnumOption[] = [
  { value: "planned", label: "Planned" },
  { value: "taken", label: "Taken" },
  { value: "cancelled", label: "Cancelled" },
];

// ── Recommendation Source (src/db/schema/recommendation-source.ts) ──
//
// Shared by the app-wide preference singleton AND the per-entry
// `activity_suggestion.source` column — both describe the same AI vs.
// curated distinction, so they use one vocabulary.
export const RECOMMENDATION_SOURCES = ["ai", "curated"] as const;
export type RecommendationSource = (typeof RECOMMENDATION_SOURCES)[number];
export const RECOMMENDATION_SOURCE_OPTIONS: readonly EnumOption[] = [
  { value: "ai", label: "AI-generated" },
  { value: "curated", label: "Manual" },
];

// ── Activity Category (EPIC-008) ──
//
// Shared by `pto_entry.category` (set by the user on the entry) AND
// `activity_suggestion.category` (tags a suggestion, AI-generated or
// curated) — both describe the same far/local/indoor distinction used to
// inform and filter suggestion generation, so they use one vocabulary.
export const ACTIVITY_CATEGORIES = ["far_travel", "local_travel", "indoor_home"] as const;
export type ActivityCategory = (typeof ACTIVITY_CATEGORIES)[number];
export const ACTIVITY_CATEGORY_OPTIONS: readonly EnumOption[] = [
  { value: "far_travel", label: "Far Travel" },
  { value: "local_travel", label: "Local Travel" },
  { value: "indoor_home", label: "Indoor/Home" },
];

/**
 * Runtime type guard for a category value read back from an API response
 * (EPIC-009's generated-suggestion picker) — those come back as plain
 * `string`, not the literal union, so this is the boundary check per
 * .claude/rules/code-quality.md ("no `as X` without a runtime type guard").
 */
export function isActivityCategory(value: string): value is ActivityCategory {
  return (ACTIVITY_CATEGORIES as readonly string[]).includes(value);
}

// ── Audit log ──
//
// `action` is the `<entity>.<verb>` string stored on every audit_log row, and
// `entityType` is the coarse kind it groups under. Both are validated against
// these allowlists in `recordAudit` (src/lib/audit.ts) and drive the filter
// dropdowns in the admin viewer. Add a new action here when you audit a new
// mutation — the helper rejects anything not listed (see .claude/rules/audit.md).

export const AUDIT_ENTITY_TYPES = [
  "widget",
  "user",
  "auth",
  "pto_entry",
  "allowance_period",
  "activity_suggestion",
  "recommendation_source",
] as const;
export type AuditEntityType = (typeof AUDIT_ENTITY_TYPES)[number];

export const AUDIT_ACTIONS = [
  // Reference-entity CRUD (src/examples/widget). Mirror this block per entity
  // you audit: `<entity>.created` / `.updated` / `.deleted`.
  "widget.created",
  "widget.updated",
  "widget.deleted",
  // PTO Entry (full CRUD).
  "pto_entry.created",
  "pto_entry.updated",
  "pto_entry.deleted",
  // EPIC-013: sending a Google Calendar invite for an entry. Not part of the
  // CRUD block above — it's a repeatable action, not a state change on the
  // row (see src/api/google-calendar-event.ts).
  "pto_entry.calendar_event_sent",
  // Allowance Period is a singleton — no create/delete, PUT only.
  "allowance_period.updated",
  // Activity Suggestion (full CRUD).
  "activity_suggestion.created",
  "activity_suggestion.updated",
  "activity_suggestion.deleted",
  // Recommendation Source is a singleton — no create/delete, PUT only.
  "recommendation_source.updated",
  // Authentication events (captured via Better Auth hooks).
  "auth.login",
  "auth.login_failed",
  "auth.logout",
  "auth.signup",
  "auth.password_reset_requested",
  "auth.password_reset_completed",
  // Admin user management (Better Auth admin plugin endpoints).
  "user.created",
  "user.deleted",
  "user.role_changed",
  "user.banned",
  "user.unbanned",
  "user.password_reset",
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];
export const AUDIT_ACTION_OPTIONS: readonly EnumOption[] = AUDIT_ACTIONS.map((value) => ({
  value,
  label: value,
}));
